import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { N8AOPass } from 'n8ao';
import { STORIES, MAIN } from './story-ui.js';

const ACCENT = new THREE.Color('#e3b87b');
const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;
const stage = document.getElementById('stage'), canvas = document.getElementById('canvas');
let renderer;
try { renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: 'high-performance' }); }
catch (e) { document.body.classList.add('room-failed'); throw e; }
renderer.setPixelRatio(Math.min(devicePixelRatio, 1.5));
renderer.setClearColor(0, 0);
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.02;
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.VSMShadowMap;
renderer.shadowMap.autoUpdate = false;
renderer.info.autoReset = false;
const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(32, 1, .1, 100);
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true; controls.dampingFactor = .075;
controls.enablePan = false; controls.minDistance = 4; controls.maxDistance = 64;
controls.minPolarAngle = .45; controls.maxPolarAngle = 1.32;
controls.minAzimuthAngle = -.1; controls.maxAzimuthAngle = 1.3;
controls.autoRotate = false;
const environment = new THREE.PMREMGenerator(renderer);
const env = environment.fromScene(new RoomEnvironment());
scene.environment = env.texture; scene.environmentIntensity = .25; environment.dispose();
scene.add(new THREE.HemisphereLight(0xd5e7ef, 0x766348, .7));
const sun = new THREE.DirectionalLight(0xffdfb6, 2.2);
sun.position.set(-3,8,6); sun.target.position.set(0,1,0); sun.castShadow = true;
sun.shadow.mapSize.set(2048,2048); sun.shadow.radius = 4; sun.shadow.blurSamples = 8;
sun.shadow.bias = -.00008; sun.shadow.normalBias = .02;
Object.assign(sun.shadow.camera,{left:-6,right:6,top:6,bottom:-6,near:.1,far:30});
scene.add(sun,sun.target);
const fill = new THREE.DirectionalLight(0xb9dcec,.75);fill.position.set(6,6,1);scene.add(fill);
const lanternLight = new THREE.PointLight(0xffcc8a,3.4,5,2);lanternLight.position.set(-.55,2.12,-.85);scene.add(lanternLight);
const wallLight = new THREE.PointLight(0xffd294,.5,2,2);wallLight.position.set(-2.8,2.6,1.72);scene.add(wallLight);
const composer = new EffectComposer(renderer);
const ao = new N8AOPass(scene,camera,1280,720);
ao.configuration.aoRadius = .35; ao.configuration.distanceFalloff = .65;
ao.configuration.intensity = 2.0; ao.configuration.gammaCorrection = false;
ao.configuration.halfRes = true; ao.setQualityMode('Medium');
composer.addPass(ao);composer.addPass(new OutputPass());
let useAO = !new URLSearchParams(location.search).has('noao');
const root = new THREE.Group();scene.add(root);
const groups = {}, allMeshes = [], clockHands = {};
function updateClock(){
  const d=new Date(), seconds=d.getSeconds(), minutes=d.getMinutes()+seconds/60, hours=d.getHours()%12+minutes/60;
  if(clockHands.hour)clockHands.hour.rotation.z=-hours*Math.PI/6;
  if(clockHands.minute)clockHands.minute.rotation.z=-minutes*Math.PI/30;
  if(clockHands.second)clockHands.second.rotation.z=-seconds*Math.PI/30;
  canvas.dataset.clockTime=d.toISOString();
}
let hovered = null, focused = null, camTween = null;
let defaultCamPos = new THREE.Vector3(), defaultTarget = new THREE.Vector3(0,1.35,0);
let visited;
try { visited = new Set(JSON.parse(sessionStorage.getItem('mj-den-visited-v2') || '[]')); }
catch { visited = new Set(); }
const loading = document.getElementById('loading'), loadingText = document.getElementById('loadingText');
const loadTimer = setInterval(()=>{ loadingText.textContent = 'Arranging the tatami room…'; },1400);

function fitDistance(radius,padding=1.12) { const vfov=THREE.MathUtils.degToRad(camera.fov),hfov=2*Math.atan(Math.tan(vfov/2)*camera.aspect);return radius/Math.sin(Math.min(vfov,hfov)/2)*padding; }
function overview() {
  // Match the selected elevated three-quarter composition, with room around the plinth.
  const dist = fitDistance(camera.aspect<.8?6.2:5.25,1.04);
  defaultTarget.set(0,1.0,0);
  defaultCamPos.copy(defaultTarget).add(new THREE.Vector3(10,8,13).normalize().multiplyScalar(dist));
  if (!focused) { camera.position.copy(defaultCamPos);controls.target.copy(defaultTarget); }
}
function tweenCamera(toPos,toTarget,duration) {
  if (!duration) {camera.position.copy(toPos);controls.target.copy(toTarget);camTween=null;return;}
  camTween={fromPos:camera.position.clone(),fromTarget:controls.target.clone(),toPos,toTarget,start:performance.now(),duration};
}
function focusOn(id) {
  const g=groups[id];if(!g)return;
  focused=id;hovered=null;controls.enabled=false;dismissHint();
  camera.clearViewOffset();
  if(stage.clientWidth<=900)camera.setViewOffset(stage.clientWidth,stage.clientHeight,0,stage.clientHeight*.30,stage.clientWidth,stage.clientHeight);
  const dist=Math.max(fitDistance(g.radius,1.42),2.2);
  const target=g.center.clone();
  const pos=target.clone().add(g.view.clone().normalize().multiplyScalar(dist));
  if(stage.clientWidth>900) {
    const right=new THREE.Vector3().subVectors(target,pos).cross(camera.up).normalize().multiplyScalar(dist*.17);
    target.add(right);pos.add(right);
  }
  tweenCamera(pos,target,REDUCED?0:850);openPanel(id);setIndexActive(id);markVisited(id);stage.classList.add('is-focused');
}
function unfocus() {
  if(!focused)return;
  focused=null;camera.clearViewOffset();controls.enabled=true;
  tweenCamera(defaultCamPos.clone(),defaultTarget.clone(),REDUCED?0:850);
  closePanel();setIndexActive(null);stage.classList.remove('is-focused');
  // the index rail was hidden while the story was open — hand focus back to its row
  const back=lastTrigger;lastTrigger=null;
  const returnTarget=stage.clientWidth<=900?indexToggle:(back?.isConnected?back:document.getElementById('tourNext'));
  requestAnimationFrame(()=>returnTarget.focus());
}
document.getElementById('reset').addEventListener('click',()=>{if(focused)unfocus();else tweenCamera(defaultCamPos.clone(),defaultTarget.clone(),REDUCED?0:750);});
addEventListener('keydown',e=>{
  if(e.key==='Escape'){ if(indexEl.classList.contains('is-open')) closeIndexSheet(true); else unfocus(); return; }
  if(e.key==='Tab'&&indexEl.classList.contains('is-open')&&stage.clientWidth<=900){
    const buttons=[...indexEl.querySelectorAll('button')];
    const first=buttons[0],last=buttons.at(-1);
    if(e.shiftKey&&document.activeElement===first){e.preventDefault();last.focus();}
    else if(!e.shiftKey&&document.activeElement===last){e.preventDefault();first.focus();}
    return;
  }
  if(e.target.closest('input,textarea,select,[contenteditable="true"]'))return;
  if(!focused||e.metaKey||e.ctrlKey||e.altKey)return;
  if(e.key==='ArrowRight'){e.preventDefault();stepTour(1);}
  else if(e.key==='ArrowLeft'){e.preventDefault();stepTour(-1);}
});
controls.addEventListener('start',()=>dismissHint());
// =====================================================================
//  WIDGETS
// =====================================================================
const tooltip = document.getElementById('tooltip');
const tipGlyph = document.getElementById('tipGlyph'), tipNum = document.getElementById('tipNum'), tipLabel = document.getElementById('tipLabel');
const hint = document.getElementById('hint');
let hintDismissed = false;
function dismissHint() { if (!hintDismissed) { hintDismissed = true; hint.classList.add('hidden'); } }
setTimeout(dismissHint, 14000);

const srStatus = document.getElementById('srStatus');
let lastTrigger = null;

// ---- story index: a ruled score sheet (right rail on desktop, sheet on narrow) ----
const indexEl = document.getElementById('index');
const indexToggle = document.getElementById('indexToggle');
const indexToggleCount = document.getElementById('indexToggleCount');
{
  const head = document.createElement('div');
  head.className = 'index__head';
  head.innerHTML = `<span class="index__title">Stories</span><span class="index__meta">${MAIN.length} stops</span>`;
  const close = document.createElement('button');
  close.type = 'button'; close.className = 'index__close'; close.setAttribute('aria-label', 'Close story list');
  close.innerHTML = '<svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true"><path d="M1 1l11 11M12 1L1 12" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
  close.addEventListener('click', () => closeIndexSheet(true));
  head.appendChild(close);
  const handle = document.createElement('div');
  handle.className = 'index__handle'; handle.setAttribute('aria-hidden', 'true');
  indexEl.append(handle, head);

  let sawMore = false;
  STORIES.forEach((s, i) => {
    if (s.group === 'more' && !sawMore) {
      sawMore = true;
      const c2 = document.createElement('div');
      c2.className = 'index__cap'; c2.textContent = 'More work';
      indexEl.appendChild(c2);
    }
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'index__item' + (s.id.startsWith('screens-') ? ' index__item--sub' : '');
    b.dataset.id = s.id;
    b.innerHTML = `<span class="num">${s.group ? '·' : String(i + 1).padStart(2, '0')}</span>` +
      `<span class="lbl">${s.label}</span><span class="mark" aria-hidden="true"></span>`;
    b.addEventListener('click', () => { lastTrigger = b; closeIndexSheet(false); focusOn(s.id); });
    b.addEventListener('mouseenter', () => { if (!focused) hovered = s.id; });
    b.addEventListener('mouseleave', () => { if (hovered === s.id) hovered = null; });
    b.addEventListener('focus', () => { if (!focused) hovered = s.id; });
    b.addEventListener('blur', () => { if (hovered === s.id) hovered = null; });
    indexEl.appendChild(b);
  });
}
function openIndexSheet() {
  indexEl.classList.add('is-open');
  indexToggle.setAttribute('aria-expanded', 'true');
  const first = indexEl.querySelector('.index__item');
  if (first) requestAnimationFrame(() => first.focus());
}
function closeIndexSheet(restoreFocus) {
  if (!indexEl.classList.contains('is-open')) return;
  indexEl.classList.remove('is-open');
  indexToggle.setAttribute('aria-expanded', 'false');
  if (restoreFocus) indexToggle.focus();
}
indexToggle.addEventListener('click', () => {
  if (indexEl.classList.contains('is-open')) closeIndexSheet(true); else openIndexSheet();
});
addEventListener('pointerdown', (e) => {
  if (!indexEl.classList.contains('is-open')) return;
  if (!indexEl.contains(e.target) && !indexToggle.contains(e.target)) closeIndexSheet(false);
}, true);
function setIndexActive(id) {
  indexEl.querySelectorAll('.index__item').forEach(el => {
    const on = el.dataset.id === id;
    el.classList.toggle('is-active', on);
    if (on) el.setAttribute('aria-current', 'true'); else el.removeAttribute('aria-current');
  });
}
function refreshIndexVisited() {
  indexEl.querySelectorAll('.index__item').forEach(el => {
    const seen = visited.has(el.dataset.id);
    el.classList.toggle('is-visited', seen);
    const label = el.querySelector('.lbl').textContent;
    el.setAttribute('aria-label', seen ? `${label} — already opened` : label);
  });
}

// ---- progress meter: the same tile face, twelve times ----
const hudTiles = document.getElementById('hudTiles'), hudCount = document.getElementById('hudCount');
MAIN.forEach(s => {
  const d = document.createElement('span');
  d.className = 'mark'; d.dataset.id = s.id;
  hudTiles.appendChild(d);
});
function refreshHud() {
  let n = 0;
  hudTiles.querySelectorAll('.mark').forEach(el => {
    const got = visited.has(el.dataset.id);
    el.classList.toggle('is-got', got);
    if (got) n++;
  });
  hudCount.innerHTML = n >= MAIN.length
    ? `<em>All ${MAIN.length}</em> stories opened`
    : `<em>${n}</em> of ${MAIN.length} stories opened`;
  indexToggleCount.textContent = `${n}/${MAIN.length}`;
}
function markVisited(id) {
  if (!MAIN.some(s => s.id === id) || visited.has(id)) { refreshIndexVisited(); return; }
  visited.add(id);
  try { sessionStorage.setItem('mj-den-visited-v2', JSON.stringify([...visited])); } catch {}
  refreshHud(); refreshIndexVisited();
}
refreshHud(); refreshIndexVisited();

// panel
const panel = document.getElementById('panel');
const panelGlyph = document.getElementById('panelGlyph'), panelNum = document.getElementById('panelNum'),
  panelEyebrow = document.getElementById('panelEyebrow'), panelTitle = document.getElementById('panelTitle'),
  panelBody = document.getElementById('panelBody'), panelCta = document.getElementById('panelCta'),
  panelWhere = document.getElementById('panelWhere'), panelNext = document.getElementById('panelNext');
document.getElementById('panelClose').addEventListener('click', () => { unfocus(); });
function openPanel(id) {
  const i = STORIES.findIndex(s => s.id === id);
  const s = STORIES[i];
  panelGlyph.textContent = s.glyph;
  panelNum.textContent = s.group ? '番外' : String(i + 1).padStart(2, '0');
  panelEyebrow.textContent = s.eyebrow;
  panelTitle.innerHTML = s.title;
  panelWhere.textContent = s.where;
  const src = document.querySelector(`#roomContent [data-room-source="${s.id}"]`);
  panelBody.innerHTML = src ? src.innerHTML : '';
  // screenshots read as plates on a wall, not as floated thumbnails
  const shots = panelBody.querySelectorAll('img');
  if (shots.length) {
    const plates = document.createElement('div');
    plates.className = 'panel__plates';
    shots.forEach(img => plates.appendChild(img));
    panelBody.appendChild(plates);
  }
  srStatus.textContent = `${s.label}. ${panelTitle.textContent} — ${s.where}.`;
  if (s.cta) {
    panelCta.href = s.cta.href; panelCta.textContent = s.cta.text;
    panelCta.classList.remove('hidden');
  } else panelCta.classList.add('hidden');
  // Keep the reading surface steady when moving between stories.
  requestAnimationFrame(() => requestAnimationFrame(() => {
    panel.classList.add('is-open');
    setTimeout(()=>{
      if(focused===id&&!panel.contains(document.activeElement))
        document.getElementById('panelClose').focus({preventScroll:true});
    },REDUCED?0:650);
  }));
  panel.setAttribute('aria-hidden', 'false');
  panel.querySelector('.panel__scroll').scrollTop = 0;
  refreshTour();
}
function closePanel() {
  panel.classList.remove('is-open');
  panel.setAttribute('aria-hidden', 'true');
  srStatus.textContent = 'Story closed. Back to the room.';
  refreshTour();
}
panelNext.addEventListener('click', () => stepTour(1));
document.getElementById('panelPrev').addEventListener('click', () => stepTour(-1));

// tour
const tourCount = document.getElementById('tourCount');
function tourIndex() { return MAIN.findIndex(s => s.id === focused); }
function stepTour(d) {
  const i = tourIndex();
  const next = i < 0 ? (d > 0 ? 0 : MAIN.length - 1) : (i + d + MAIN.length) % MAIN.length;
  focusOn(MAIN[next].id);
}
document.getElementById('tourNext').addEventListener('click', () => stepTour(1));
document.getElementById('tourPrev').addEventListener('click', () => stepTour(-1));
function refreshTour() {
  const i = tourIndex();
  tourCount.innerHTML = i < 0
    ? `<b>Start</b> · ${MAIN.length} stops`
    : `<b>${String(i + 1).padStart(2, '0')}</b> / ${MAIN.length} · ${MAIN[i].label}`;
}
refreshTour();

// =====================================================================
//  POINTER
// =====================================================================
const raycaster = new THREE.Raycaster();
const pointer = new THREE.Vector2();
let pointerClient = { x: 0, y: 0 };
let downAt = null;
canvas.addEventListener('pointermove', (e) => {
  const r = canvas.getBoundingClientRect();
  pointer.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
  pointerClient = { x: e.clientX - r.left, y: e.clientY - r.top };
  if (focused) return;
  raycaster.setFromCamera(pointer, camera);
  const hit = raycaster.intersectObjects(allMeshes, false)[0];
  hovered = hit?.object.userData.hotspot || null;
});
canvas.addEventListener('pointerdown', (e) => { downAt = { x: e.clientX, y: e.clientY, t: performance.now() }; });
canvas.addEventListener('pointerup', (e) => {
  if (!downAt) return;
  const moved = Math.hypot(e.clientX - downAt.x, e.clientY - downAt.y);
  const dt = performance.now() - downAt.t;
  downAt = null;
  if (moved > 6 || dt > 450) return;
  const r = canvas.getBoundingClientRect();
  pointer.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
  raycaster.setFromCamera(pointer, camera);
  const hit = raycaster.intersectObjects(allMeshes, false)[0];
  if (hit?.object.userData.hotspot) { lastTrigger = null; focusOn(hit.object.userData.hotspot); }
  else if (focused) unfocus();
});


const views={
  game:[.55,.95,1],community:[.65,.8,1],architecture:[1,.25,.45],
  'screens-dash':[1,.1,.12],'screens-events':[1,.1,.12],'screens-board':[1,.1,.12],
  contact:[.45,.8,1],location:[.4,.25,1],services:[.3,.2,1],realtime:[.15,.1,1],stats:[.2,.1,1],journey:[.3,.2,1],atelier:[.3,.2,1],acoem:[.3,.2,1]
};
async function init() {
  try {
    const draco=new DRACOLoader().setDecoderPath('/assets/mahjong-room/draco/').setWorkerLimit(2);
    const loader=new GLTFLoader().setDRACOLoader(draco);
    const [gltf,manifest]=await Promise.all([loader.loadAsync('/assets/mahjong-room/tatami-room.glb'),fetch('/assets/mahjong-room/manifest.json').then(r=>{if(!r.ok)throw Error('Room manifest unavailable');return r.json();})]);
    draco.dispose();root.add(gltf.scene);
    root.traverse(o=>{
      if(!o.isMesh)return;
      o.castShadow=true;o.receiveShadow=true;
      if(o.material.name==='hinoki')o.material.color.set('#d6ab76');
      if(o.name.startsWith('clock-hour'))clockHands.hour=o;
      if(o.name.startsWith('clock-minute'))clockHands.minute=o;
      if(o.name.startsWith('clock-second'))clockHands.second=o;
      if(o.name.startsWith('clock-'))o.castShadow=false;
      allMeshes.push(o);
      const id=o.userData.hotspot;
      if(id)(groups[id]??={meshes:[],view:new THREE.Vector3(...(views[id]||[.4,.3,1]))}).meshes.push(o);
    });
    updateClock();
    canvas.dataset.gallery=JSON.stringify(allMeshes.filter(o=>o.name.startsWith("screen-")).map(o=>({name:o.name,texture:o.material.map?.image?.src,uv:Array.from(o.geometry.attributes.uv.array)})));
    root.updateMatrixWorld(true);
    for(const [id,g] of Object.entries(groups)) {
      const b=new THREE.Box3();for(const m of g.meshes)b.expandByObject(m);
      const sphere=b.getBoundingSphere(new THREE.Sphere());g.center=sphere.center;g.radius=Math.max(.25,sphere.radius);
      if(id==='community'){g.center.set(0,.9,2.28);g.radius=.72;}
      if(id==='game'){g.center.set(0,1.05,.43);g.radius=1.38;}
      if(id==='location'){g.center.set(-.55,2.61,-.85);g.radius=.5;}
    }
    for(const o of allMeshes) {
      // One copy per batch, not per small decorative component.
      o.material=o.material.clone();o.material.userData.baseColor=o.material.color.clone();
      o.material.userData.baseEmissive=o.material.emissive.clone();
    }
    resize();renderer.shadowMap.needsUpdate=true;
    await renderer.compileAsync(scene,camera);
    clearInterval(loadTimer);loading.classList.add('is-leaving');setTimeout(()=>loading.style.display='none',1300);
    metrics.ready=true;metrics.geometryTriangles=manifest.triangles;metrics.tileCount=manifest.tileCount;
    requestAnimationFrame(loop);
  } catch(e) {clearInterval(loadTimer);document.body.classList.add('room-failed');console.error('Mahjong room failed',e);}
}
const metrics={ready:false,fps:0,p95FrameMs:0,drawCalls:0,triangles:0,ao:true,pixelRatio:renderer.getPixelRatio()};
let lastTime=0,reportTime=0,frames=[],renderCost=[];
let lastHover=null,lastFocus=null,lastClockSecond=-1;
let tipFor=null,tipSize=[0,0];
function loop(now) {
  requestAnimationFrame(loop);
  if(document.hidden){lastTime=0;return;}
  const elapsed=lastTime?now-lastTime:16.67;lastTime=now;
  if(camTween){const k=Math.min(1,(now-camTween.start)/camTween.duration),v=k*k*(3-2*k);camera.position.lerpVectors(camTween.fromPos,camTween.toPos,v);controls.target.lerpVectors(camTween.fromTarget,camTween.toTarget,v);if(k===1)camTween=null;}
  controls.update();
  if(Math.floor(now/1000)!==lastClockSecond){lastClockSecond=Math.floor(now/1000);updateClock();}
  if(hovered!==lastHover||focused!==lastFocus) {
    for(const o of allMeshes){const m=o.material;m.color.copy(m.userData.baseColor);m.emissive.copy(m.userData.baseEmissive);if(hovered&&o.userData.hotspot===hovered)m.emissive.lerp(ACCENT,.12);}
    lastHover=hovered;lastFocus=focused;
  }
  if(hovered&&!focused&&!indexEl.matches(':hover')&&!indexEl.contains(document.activeElement)){
    if(hovered!==tipFor){
      const s=STORIES.find(s=>s.id===hovered);
      tipGlyph.textContent=s.glyph;tipNum.textContent=String(STORIES.indexOf(s)+1).padStart(2,'0');tipLabel.textContent=s.label;
      tooltip.classList.remove('hidden');
      tipFor=hovered;tipSize=[tooltip.offsetWidth,tooltip.offsetHeight];
    }
    tooltip.classList.remove('hidden');stage.classList.add('is-hovering');
    // keep the label inside the stage — flip left / drop below near the edges
    const w=tipSize[0],h=tipSize[1],pad=12;
    let x=pointerClient.x+16,y=pointerClient.y-h-14;
    if(x+w>stage.clientWidth-pad)x=pointerClient.x-16-w;
    if(y<pad)y=pointerClient.y+20;
    tooltip.style.left=Math.max(pad,Math.min(x,stage.clientWidth-w-pad))+'px';
    tooltip.style.top=Math.max(pad,Math.min(y,stage.clientHeight-h-pad))+'px';
  }
  else{tooltip.classList.add('hidden');stage.classList.remove('is-hovering');tipFor=null;}
  renderer.info.reset();
  const before=performance.now();
  if(useAO){try{composer.render();}catch(e){console.warn('AO unavailable; using direct renderer',e);useAO=false;renderer.render(scene,camera);}}else renderer.render(scene,camera);
  renderCost.push(performance.now()-before);frames.push(elapsed);
  if(now-reportTime>2000&&frames.length>20) {
    const sorted=frames.slice().sort((a,b)=>a-b);
    metrics.focused=focused;metrics.viewport=[stage.clientWidth,stage.clientHeight];
    metrics.fps=Math.round(1000/(frames.reduce((a,b)=>a+b,0)/frames.length));metrics.p95FrameMs=+sorted[Math.floor(sorted.length*.95)].toFixed(1);
    metrics.cpuRenderMs=+(renderCost.reduce((a,b)=>a+b,0)/renderCost.length).toFixed(2);
    metrics.drawCalls=renderer.info.render.calls;metrics.triangles=renderer.info.render.triangles;metrics.ao=useAO;
    canvas.dataset.roomStats=JSON.stringify(metrics);
    frames=[];renderCost=[];reportTime=now;
  }
}
function resize(){const w=stage.clientWidth,h=stage.clientHeight;renderer.setSize(w,h,false);composer.setSize(w,h);camera.aspect=w/h;camera.updateProjectionMatrix();overview();if(focused)focusOn(focused);}
addEventListener('resize',resize);
window.__den={renderer,scene,camera,root,controls,groups,focusOn,unfocus,metrics};
init();
